import React, { memo, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import ConsentDocumentStep from 'modules/views/shared/ConsentDocumentStep';
import { WelcomeModal } from './WelcomeModal';

import { useConsentFormContext } from '../context/ConsentFormContext';
import { useAppSelector } from 'modules/state-mgmt/store';
import { selectCustomConsentAgreements } from 'modules/state-mgmt/cache';
import { WorkerInvitationStatus } from 'models/general';
import { useProjectAcceptance } from '../hooks/useProjectAcceptance';

export const CustomConsentFormStep = memo(() => {
  const { t } = useTranslation();
  const {
    prevStep,
    firstStep,
    workerId,
    workerUserId,
    projectId,
    projectName,
    languageCode,
    getFormPayloads,
    setIsLoading: setConsentFormLoading,
  } = useConsentFormContext();

  const [currentFormIndex, setCurrentFormIndex] = useState(0);
  const [isChecked, setIsChecked] = useState(false);
  const [isSignatureApplied, setIsSignatureApplied] = useState(false);

  const { signatureUrl, tempSignature, isOffline, isWorker, isUnverified } = useAppSelector(state => ({
    signatureUrl: state.onboarding.signatureUrl,
    tempSignature: state.onboarding.temp?.signature,
    isOffline: !state.app.isOnline || state.onboarding?.uploadingDetails?.isOffline,
    isWorker: !state.user?.userId || state.user?.isWorker,
    isUnverified: state.onboarding?.invitationStatus === WorkerInvitationStatus.NOT_INVITED,
  }));

  // Offline, a remote (S3) signature URL cannot load and the Apply Signature preview would render
  // the broken-image fallback. Prefer the locally captured base64 signature when we have one.
  const isRemoteSignature = !!signatureUrl && !signatureUrl.startsWith('data:');
  const displaySignatureUrl = isOffline && isRemoteSignature ? tempSignature || signatureUrl : signatureUrl;

  const customForms = useAppSelector(state => selectCustomConsentAgreements(state, projectId));

  const { executeAcceptance, onboardingStatus, onCloseWelcomeModal, isLoading } = useProjectAcceptance({
    workerId,
    workerUserId,
    projectId,
    projectName,
    languageCode,
    getFormPayloads,
    isUnverified,
    isOffline,
    isWorker,
    firstStep,
  });

  useEffect(() => {
    setConsentFormLoading(isLoading);
  }, [isLoading, setConsentFormLoading]);

  const currentForm = customForms[currentFormIndex];
  const isLastForm = customForms.length === 0 || currentFormIndex === customForms.length - 1;

  // Keep the last known form in a ref so it stays visible while APIs are in flight.
  // Once isLoading is false, we revert to currentForm (which may be undefined if all
  // forms are now accepted — letting the guard below fire normally).
  const lastFormRef = useRef(currentForm ?? null);
  if (currentForm) lastFormRef.current = currentForm;
  const displayForm = isLoading ? (currentForm ?? lastFormRef.current) : currentForm;

  const resetFormState = () => {
    setIsChecked(false);
    setIsSignatureApplied(false);
  };

  const onBack = () => {
    if (currentFormIndex === 0) {
      prevStep();
    } else {
      setCurrentFormIndex(i => i - 1);
      resetFormState();
    }
  };

  const onNext = () => {
    if (!isLastForm) {
      setCurrentFormIndex(i => i + 1);
      resetFormState();
      return;
    }

    const consentAgreementContentIds = customForms.map(f => f.consentAgreementContentId);
    const consentAgreementIds = customForms.map(f => f.consentAgreementId);
    executeAcceptance({ consentAgreementIds, consentAgreementContentIds, signatureUrl });
  };

  // After executeAcceptance, fetchConsentAgreements marks all forms accepted so
  // customForms becomes [] and currentForm becomes undefined. Guard only when there
  // is also no WelcomeModal to show — otherwise the modal would never render.
  if (!displayForm && !onboardingStatus) return null;

  return (
    <>
      {displayForm && (
        <ConsentDocumentStep
          htmlContent={displayForm.htmlContent}
          checkboxLabel={t('registrationConsent.authorizationCheckbox')}
          isChecked={isChecked}
          onCheckChange={setIsChecked}
          isSignatureApplied={isSignatureApplied}
          onSignatureAppliedChange={setIsSignatureApplied}
          onBack={onBack}
          onNext={onNext}
          isLoading={isLoading}
          signatureUrl={displaySignatureUrl}
          nextButtonLabel={isLastForm ? t('consentForm.step2.completeRegistration') : undefined}
          withoutWizardWrapper
        />
      )}
      {!!onboardingStatus && (
        <WelcomeModal
          open={!!onboardingStatus}
          onClose={onCloseWelcomeModal}
          projectName={projectName}
          isCertificationComplete={onboardingStatus.isCertificationComplete}
          isCoursesComplete={onboardingStatus.isCoursesComplete}
          isDrugTestingComplete={onboardingStatus.isDrugTestingComplete}
          isUnverified={isUnverified}
        />
      )}
    </>
  );
});
